/* eslint-disable @typescript-eslint/no-explicit-any */
import { ElementRef, ReactNode, forwardRef } from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';

import { Color, cn } from 'styles';
import { ChangeEventLike, buildChangeEventLike } from './types';

const colorClasses = {
  primary: 'data-[state=checked]:bg-primary data-[state=checked]:text-primary',
  secondary: 'data-[state=checked]:bg-secondary data-[state=checked]:text-secondary',
  success: 'data-[state=checked]:bg-success data-[state=checked]:text-success',
  destructive: 'data-[state=checked]:bg-destructive data-[state=checked]:text-destructive',
  info: 'data-[state=checked]:bg-info data-[state=checked]:text-info',
};

const switchVariants = {
  tiny: {
    container: 'h-4 w-8',
    thumb: 'h-3 w-3 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
  },

  small: {
    container: 'h-5 w-10',
    thumb: 'h-4 w-4 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
  },

  medium: {
    container: 'h-6 w-11',
    thumb: 'h-5 w-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
  },

  large: {
    container: 'h-8 w-14',
    thumb: 'h-7 w-7 data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0',
  },
};

type SwitchSize = keyof typeof switchVariants;

interface SwitchProps {
  className?: string;
  thumbClassName?: string;
  checked?: boolean;
  defaultChecked?: boolean;

  color?: Extract<Color, 'primary' | 'secondary' | 'success' | 'destructive' | 'info'>;

  size?: SwitchSize;

  children?: ReactNode;

  disabled?: boolean;
  name?: string;
  id?: string;
  onChangeDirect?: (newChecked: boolean) => any;
  onChange?: (eventLike: ChangeEventLike<boolean>) => any;
}

export const Switch = forwardRef<ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  (
    {
      className,
      onChangeDirect,
      onChange,
      color = 'primary',
      size = 'medium',
      thumbClassName,
      children,
      ...props
    },
    ref,
  ) => {
    const { container, thumb } = switchVariants[size];

    const handleChange = (value: boolean): void => {
      onChangeDirect?.(value);
      onChange?.(
        buildChangeEventLike({
          name: props.name,
          id: props.name,
          value,
          checked: value,
        }),
      );
    };

    return (
      <SwitchPrimitives.Root
        {...props}
        ref={ref}
        onCheckedChange={handleChange}
        className={cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
          `${colorClasses[color]} data-[state=unchecked]:bg-input`,
          container,
          'disabled:cursor-not-allowed disabled:opacity-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          className,
        )}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none flex items-center justify-center rounded-full bg-background shadow-lg ring-0 transition-transform',
            thumb,
            thumbClassName,
          )}
        >
          {children}
        </SwitchPrimitives.Thumb>
      </SwitchPrimitives.Root>
    );
  },
);

Switch.displayName = SwitchPrimitives.Root.displayName;
